import { apiClient, API_ENDPOINTS } from './api';
import { cacheService } from './cacheService';

class EmployeeCoursesSummaryService {
  // Ключ для кеширования
  CACHE_KEY = 'employee_courses_summary';
  
  // Флаг для отслеживания загрузки (предотвращение дублирования)
  isLoading = false;
  
  // Очередь запросов, ожидающих завершения текущей загрузки
  pendingRequests = [];

  /**
   * Получение комплексной сводки по обучениям сотрудников
   * @param {boolean} forceRefresh - принудительное обновление кеша
   * @returns {Promise<Object>} - данные сводки
   */
  async getSummary(forceRefresh = false) {
    const cacheKey = cacheService.getCacheKey(this.CACHE_KEY);
    
    if (!forceRefresh) {
      const cached = cacheService.get(cacheKey, 15 * 60 * 1000);
      if (cached) {
        console.log('Возвращаем кешированные данные employee-courses-summary');
        return cached;
      }
    }
    
    if (this.isLoading) {
      console.log('Загрузка уже выполняется, добавляем в очередь');
      return new Promise((resolve, reject) => {
        this.pendingRequests.push({ resolve, reject });
      });
    }
    
    this.isLoading = true;
    
    try {
      console.log('Загрузка данных с эндпоинта /trainings/employee-courses-summary');
      const response = await apiClient.get(API_ENDPOINTS.TRAININGS_EMPLOYEE_COURSES_SUMMARY);
      
      const normalizedData = this.normalizeResponse(response);
      
      cacheService.set(cacheKey, normalizedData);
      
      this.pendingRequests.forEach(request => {
        request.resolve(normalizedData);
      });
      this.pendingRequests = [];
      
      return normalizedData;
    } catch (error) {
      console.error('Ошибка загрузки сводки по обучениям:', error);
      
      this.pendingRequests.forEach(request => {
        request.reject(error);
      });
      this.pendingRequests = [];
      
      throw error;
    } finally {
      this.isLoading = false;
    }
  }
  
  /**
   * Нормализация ответа сервера
   */
  normalizeResponse(response) {
    const data = response?.data || response;
    
    if (!data) {
      console.warn('Пустой ответ от сервера');
      return this.getEmptySummary();
    }
    
    // Если данные уже в правильном формате
    if (data.expired && data.expiring_in_month && data.expiring_in_two_months && data.statistics) {
      const result = {
        expired: {
          total: data.expired.total || 0,
          byCourse: this.processByCourseData(data.expired.by_course || []),
          employeesList: this.extractEmployeesFromByCourse(data.expired.by_course || [])
        },
        expiringInMonth: {
          total: data.expiring_in_month.total || 0,
          byCourse: this.processByCourseData(data.expiring_in_month.by_course || []),
          employeesList: this.extractEmployeesFromByCourse(data.expiring_in_month.by_course || [])
        },
        expiringInTwoMonths: {
          total: data.expiring_in_two_months.total || 0,
          byCourse: this.processByCourseData(data.expiring_in_two_months.by_course || []),
          employeesList: this.extractEmployeesFromByCourse(data.expiring_in_two_months.by_course || [])
        },
        required: {
          total: data.required?.total || 0,
          byCourse: this.processByCourseData(data.required?.by_course || []),
          employeesList: this.extractEmployeesFromByCourse(data.required?.by_course || [])
        },
        statistics: {
          totalTrainings: data.statistics.total_trainings || data.statistics.totalTrainings || 0,
          expiredTotal: data.statistics.expired_total || data.statistics.expiredTotal || 0,
          expiringMonthTotal: data.statistics.expiring_month_total || data.statistics.expiringMonthTotal || 0,
          expiringTwoMonthsTotal: data.statistics.expiring_two_months_total || data.statistics.expiringTwoMonthsTotal || 0,
          requiredTotal: data.statistics.required_total || data.statistics.requiredTotal || 0,
          activeTotal: data.statistics.active_total || data.statistics.activeTotal || 0,
          uniqueEmployeesWithExpired: data.statistics.unique_employees_with_expired || data.statistics.uniqueEmployeesWithExpired || 0,
          uniqueEmployeesExpiringMonth: data.statistics.unique_employees_expiring_month || data.statistics.uniqueEmployeesExpiringMonth || 0,
          uniqueEmployeesExpiringTwoMonths: data.statistics.unique_employees_expiring_two_months || data.statistics.uniqueEmployeesExpiringTwoMonths || 0,
          uniqueEmployeesRequired: data.statistics.unique_employees_required || data.statistics.uniqueEmployeesRequired || 0,
          registeredForEvents: data.statistics.registered_for_events || data.statistics.registeredForEvents || { expired: 0, expiring_month: 0, expiring_two_months: 0, required: 0 }
        }
      };
      
      return result;
    }
    
    console.warn('Неожиданный формат ответа от сервера:', data);
    return this.getEmptySummary();
  }
  
  /**
   * Обработка данных by_course для унификации полей
   */
  processByCourseData(byCourse) {
    if (!Array.isArray(byCourse)) {
      console.warn('byCourse is not an array:', byCourse);
      return [];
    }
    
    return byCourse.map(course => {
      const totalCount = course.total_count || course.totalCount || course.count || 0;
      const registeredCount = course.registered_count || course.registeredCount || 0;
      const notRegisteredCount = course.not_registered_count !== undefined 
        ? course.not_registered_count 
        : course.notRegisteredCount !== undefined 
          ? course.notRegisteredCount 
          : (totalCount - registeredCount);
      
      return {
        course_id: course.course_id || course.courseId,
        course_name: course.course_name || course.courseName || 'Без названия',
        course_category: course.course_category || course.courseCategory || 'Без категории',
        total_count: totalCount,
        registered_count: registeredCount,
        not_registered_count: notRegisteredCount,
        count: totalCount,
        employees: Array.isArray(course.employees) ? course.employees.map(emp => ({
          employee_id: emp.employee_id || emp.employeeId,
          employee_name: emp.employee_name || emp.employeeName,
          personnel_number: emp.personnel_number || emp.personnelNumber,
          position: emp.position,
          position_id: emp.position_id || emp.positionId,
          department: emp.department,
          department_id: emp.department_id || emp.departmentId,
          days_left: emp.days_left || emp.daysLeft || 0,
          days_overdue: emp.days_overdue || emp.daysOverdue || 0,
          expiration_date: emp.expiration_date || emp.expirationDate,
          registered_for_event: emp.registered_for_event || emp.registeredForEvent || false,
          event: emp.event || null,
          training_id: emp.training_id || null
        })) : []
      };
    });
  }

  /**
   * Возвращает пустую структуру данных
   */
  getEmptySummary() {
    return {
      expired: { total: 0, byCourse: [], employeesList: [] },
      expiringInMonth: { total: 0, byCourse: [], employeesList: [] },
      expiringInTwoMonths: { total: 0, byCourse: [], employeesList: [] },
      required: { total: 0, byCourse: [], employeesList: [] },
      statistics: {
        totalTrainings: 0,
        expiredTotal: 0,
        expiringMonthTotal: 0,
        expiringTwoMonthsTotal: 0,
        requiredTotal: 0,
        activeTotal: 0,
        uniqueEmployeesWithExpired: 0,
        uniqueEmployeesExpiringMonth: 0,
        uniqueEmployeesExpiringTwoMonths: 0,
        uniqueEmployeesRequired: 0,
        registeredForEvents: { expired: 0, expiring_month: 0, expiring_two_months: 0, required: 0 }
      }
    };
  }

  /**
   * Извлечение списка сотрудников из byCourse
   */
  extractEmployeesFromByCourse(byCourse) {
    const employees = [];
    if (!Array.isArray(byCourse)) return employees;
    
    for (const course of byCourse) {
      if (course.employees && Array.isArray(course.employees)) {
        for (const emp of course.employees) {
          employees.push({
            employee_id: emp.employee_id || emp.employeeId,
            employee_name: emp.employee_name || emp.employeeName,
            personnel_number: emp.personnel_number || emp.personnelNumber,
            position: emp.position,
            position_id: emp.position_id || emp.positionId,
            department: emp.department,
            department_id: emp.department_id || emp.departmentId,
            course_id: course.course_id || course.courseId,
            course_name: course.course_name || course.courseName,
            course_category: course.course_category || course.courseCategory,
            days_left: emp.days_left || emp.daysLeft || 0,
            days_overdue: emp.days_overdue || emp.daysOverdue || 0,
            expiration_date: emp.expiration_date || emp.expirationDate,
            registered_for_event: emp.registered_for_event || emp.registeredForEvent || false,
            event: emp.event || null,
            training_id: emp.training_id || null
          });
        }
      }
    }
    return employees;
  }
  
  /**
   * Получение списка элементов для блока "Требуют внимания"
   */
  getAttentionItems(summaryData) {
    const items = [];
    
    if (!summaryData) return items;
    
    // Добавляем просроченные обучения (expired)
    if (summaryData.expired && summaryData.expired.employeesList) {
      summaryData.expired.employeesList.forEach(record => {
        items.push({
          id: `expired_${record.employee_id}_${record.course_id}`,
          trainingId: record.training_id,
          employeeId: record.employee_id,
          employeeName: record.employee_name,
          personnelNumber: record.personnel_number,
          position: record.position,
          positionId: record.position_id,
          department: record.department,
          departmentId: record.department_id,
          courseId: record.course_id,
          courseName: record.course_name,
          courseCategory: record.course_category,
          status: 'overdue',
          daysLeft: record.days_left || 0,
          daysOverdue: record.days_overdue || 0,
          expirationDate: record.expiration_date,
          // УРОВЕНЬ НАПРЯМУЮ ИЗ ТИПА ОТВЕТА ЭНДПОЙНТА
          level: 'expired',
          type: 'expired',
          registeredForEvent: record.registered_for_event || false,
          eventInfo: record.event || null
        });
      });
    }
    
    // Добавляем истекающие в месяц (expiring_in_month)
    if (summaryData.expiringInMonth && summaryData.expiringInMonth.employeesList) {
      summaryData.expiringInMonth.employeesList.forEach(record => {
        items.push({
          id: `expiring_month_${record.employee_id}_${record.course_id}`,
          trainingId: record.training_id,
          employeeId: record.employee_id,
          employeeName: record.employee_name,
          personnelNumber: record.personnel_number,
          position: record.position,
          positionId: record.position_id,
          department: record.department,
          departmentId: record.department_id,
          courseId: record.course_id,
          courseName: record.course_name,
          courseCategory: record.course_category,
          status: 'expiring',
          daysLeft: record.days_left || 0,
          daysOverdue: 0,
          expirationDate: record.expiration_date,
          // УРОВЕНЬ НАПРЯМУЮ ИЗ ТИПА ОТВЕТА ЭНДПОЙНТА
          level: 'expiring_month',
          type: 'expiring_month',
          registeredForEvent: record.registered_for_event || false,
          eventInfo: record.event || null
        });
      });
    }
    
    // Добавляем истекающие через 1-2 месяца (expiring_in_two_months)
    if (summaryData.expiringInTwoMonths && summaryData.expiringInTwoMonths.employeesList) {
      summaryData.expiringInTwoMonths.employeesList.forEach(record => {
        items.push({
          id: `expiring_two_months_${record.employee_id}_${record.course_id}`,
          trainingId: record.training_id,
          employeeId: record.employee_id,
          employeeName: record.employee_name,
          personnelNumber: record.personnel_number,
          position: record.position,
          positionId: record.position_id,
          department: record.department,
          departmentId: record.department_id,
          courseId: record.course_id,
          courseName: record.course_name,
          courseCategory: record.course_category,
          status: 'planning',
          daysLeft: record.days_left || 0,
          daysOverdue: 0,
          expirationDate: record.expiration_date,
          // УРОВЕНЬ НАПРЯМУЮ ИЗ ТИПА ОТВЕТА ЭНДПОЙНТА
          level: 'expiring_two_months',
          type: 'expiring_two_months',
          registeredForEvent: record.registered_for_event || false,
          eventInfo: record.event || null
        });
      });
    }
    
    // Добавляем требуемые обучения (required)
    if (summaryData.required && summaryData.required.employeesList) {
      summaryData.required.employeesList.forEach(record => {
        items.push({
          id: `required_${record.employee_id}_${record.course_id}`,
          trainingId: record.training_id,
          employeeId: record.employee_id,
          employeeName: record.employee_name,
          personnelNumber: record.personnel_number,
          position: record.position,
          positionId: record.position_id,
          department: record.department,
          departmentId: record.department_id,
          courseId: record.course_id,
          courseName: record.course_name,
          courseCategory: record.course_category,
          status: 'required',
          daysLeft: null,
          daysOverdue: 0,
          expirationDate: null,
          // УРОВЕНЬ НАПРЯМУЮ ИЗ ТИПА ОТВЕТА ЭНДПОЙНТА
          level: 'required',
          type: 'required',
          registeredForEvent: record.registered_for_event || false,
          eventInfo: record.event || null
        });
      });
    }
    
    // Сортируем: сначала просроченные, потом требуемые, потом по дням
    return items.sort((a, b) => {
      if (a.type === 'expired' && b.type !== 'expired') return -1;
      if (a.type !== 'expired' && b.type === 'expired') return 1;
      if (a.type === 'required' && b.type !== 'required' && b.type !== 'expired') return -1;
      if (a.type !== 'required' && b.type === 'required' && a.type !== 'expired') return 1;
      if (a.type === 'expired') return b.daysOverdue - a.daysOverdue;
      if (a.type === 'required') return 0;
      return a.daysLeft - b.daysLeft;
    });
  }
  
  /**
   * Получение топ курсов для просроченных (для карточки)
   * @deprecated Используйте getCardStatistics вместо этого метода
   */
  getTopExpiredCourses(summaryData, limit = 3) {
    if (!summaryData || !summaryData.expired || !summaryData.expired.byCourse) {
      return [];
    }
    
    const courses = summaryData.expired.byCourse
      .map(course => {
        const totalCount = course.total_count || course.count || 0;
        const registeredCount = course.registered_count || 0;
        const notRegisteredCount = course.not_registered_count !== undefined 
          ? course.not_registered_count 
          : (totalCount - registeredCount);
        
        return {
          id: course.course_id,
          name: course.course_name || 'Без названия',
          category: course.course_category || 'Без категории',
          totalCount: totalCount,
          registeredCount: registeredCount,
          notRegisteredCount: notRegisteredCount,
          count: totalCount
        };
      })
      .sort((a, b) => b.notRegisteredCount - a.notRegisteredCount || b.totalCount - a.totalCount)
      .slice(0, limit);
    
    console.log('Top expired courses:', courses);
    return courses;
  }
  
  /**
   * Получение топ курсов для истекающих в месяц (для карточки)
   * @deprecated Используйте getCardStatistics вместо этого метода
   */
  getTopExpiringCourses(summaryData, limit = 3) {
    if (!summaryData || !summaryData.expiringInMonth || !summaryData.expiringInMonth.byCourse) {
      return [];
    }
    
    const courses = summaryData.expiringInMonth.byCourse
      .map(course => {
        const totalCount = course.total_count || course.count || 0;
        const registeredCount = course.registered_count || 0;
        const notRegisteredCount = course.not_registered_count !== undefined 
          ? course.not_registered_count 
          : (totalCount - registeredCount);
        
        return {
          id: course.course_id,
          name: course.course_name || 'Без названия',
          category: course.course_category || 'Без категории',
          totalCount: totalCount,
          registeredCount: registeredCount,
          notRegisteredCount: notRegisteredCount,
          count: totalCount,
          minDaysLeft: Math.min(...(course.employees || []).map(e => e.days_left || 999))
        };
      })
      .sort((a, b) => b.notRegisteredCount - a.notRegisteredCount || b.totalCount - a.totalCount)
      .slice(0, limit);
    
    console.log('Top expiring courses:', courses);
    return courses;
  }
  
  /**
   * Получение статистики для карточек на основе attentionItems
   * Гарантирует консистентность данных между карточками и списком "Требуют внимания"
   * @param {Object} summaryData - данные сводки
   * @param {Array} attentionItems - опционально, список элементов внимания (если не передан, будет создан из summaryData)
   * @returns {Object} - агрегированная статистика по категориям
   */
  getCardStatistics(summaryData, attentionItems = null) {
    if (!attentionItems) {
      attentionItems = this.getAttentionItems(summaryData);
    }
    
    const stats = {
      expired: {
        byCourse: {},
        total: 0,
        totalRegistered: 0,
        totalNotRegistered: 0
      },
      expiringInMonth: {
        byCourse: {},
        total: 0,
        totalRegistered: 0,
        totalNotRegistered: 0
      },
      expiringInTwoMonths: {
        byCourse: {},
        total: 0,
        totalRegistered: 0,
        totalNotRegistered: 0
      },
      required: {
        byCourse: {},
        total: 0,
        totalRegistered: 0,
        totalNotRegistered: 0
      }
    };
    
    attentionItems.forEach(item => {
      let category;
      
      if (item.type === 'expired') {
        category = stats.expired;
      } else if (item.type === 'expiring_month') {
        category = stats.expiringInMonth;
      } else if (item.type === 'expiring_two_months') {
        category = stats.expiringInTwoMonths;
      } else if (item.type === 'required') {
        category = stats.required;
      } else {
        console.warn('Неизвестный тип элемента:', item.type);
        return;
      }
      
      category.total++;
      if (item.registeredForEvent) {
        category.totalRegistered++;
      } else {
        category.totalNotRegistered++;
      }
      
      if (!category.byCourse[item.courseId]) {
        category.byCourse[item.courseId] = {
          id: item.courseId,
          name: item.courseName,
          category: item.courseCategory,
          total: 0,
          registered: 0,
          notRegistered: 0
        };
      }
      
      category.byCourse[item.courseId].total++;
      if (item.registeredForEvent) {
        category.byCourse[item.courseId].registered++;
      } else {
        category.byCourse[item.courseId].notRegistered++;
      }
    });
    
    stats.expired.byCourse = Object.values(stats.expired.byCourse)
      .sort((a, b) => b.notRegistered - a.notRegistered || b.total - a.total);
    
    stats.expiringInMonth.byCourse = Object.values(stats.expiringInMonth.byCourse)
      .sort((a, b) => b.notRegistered - a.notRegistered || b.total - a.total);
    
    stats.expiringInTwoMonths.byCourse = Object.values(stats.expiringInTwoMonths.byCourse)
      .sort((a, b) => b.notRegistered - a.notRegistered || b.total - a.total);
    
    stats.required.byCourse = Object.values(stats.required.byCourse)
      .sort((a, b) => b.notRegistered - a.notRegistered || b.total - a.total);
    
    return stats;
  }
  
  /**
   * Инвалидация кеша
   */
  invalidateCache() {
    const cacheKey = cacheService.getCacheKey(this.CACHE_KEY);
    cacheService.invalidate(cacheKey);
    console.log('Кеш employee-courses-summary инвалидирован');
  }
}

export const employeeCoursesSummaryService = new EmployeeCoursesSummaryService();